import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import VendorCombobox from '../components/VendorCombobox/VendorCombobox';
import ProjectTagCombobox from '../components/ProjectTagCombobox/ProjectTagCombobox';
import RecurrencePopup from '../components/RecurrencePopup/RecurrencePopup';
import { api, DUPLICATE_RECEIPT_TOAST, isDuplicateReceiptError } from '../services/api';
import { prepareInvoiceFile, PreparedInvoice } from '../utils/jpegFromInvoice';
import {
  clipboardImageFiles,
  extractHttpUrls,
  splitDroppedInvoiceFiles,
} from '../utils/invoiceLink';
import { Expense } from '../types';
import { FlatCategory } from '../types/category';
import { getSubcategories, findCategoryByName } from '../services/categoryService';
import { formatMetricaDate } from '../utils/dateUtils';
import MetricaDateInput from '../components/MetricaDateInput/MetricaDateInput';
import SearchableSelect, { optionsFromNames } from '../components/SearchableSelect/SearchableSelect';
import { FIELD_LABELS, getCriticalMissingFields } from '../utils/fieldLabels';
import { defaultRecurrenceConfig, exceedsTwoYearHorizon, recurrenceCompletionMessage, RecurrenceConfig } from '../types/recurrence';
import { noticeForMissingData } from '../utils/missingDataNotice';
import MoneyInput from '../components/MoneyInput/MoneyInput';
import {
  amountsForNoVat,
  amountsFromNet,
  amountsFromTotal,
  amountsFromVat,
} from '../utils/amountUtils';
import { NO_VAT_DISPLAY, withCategoryAmountSync } from '../utils/noVat';
import editorStyles from './ExpenseEditorPage.module.css';
import styles from './UploadPage.module.css';

const requiredFields: (keyof Expense)[] = ['vendorName', 'amount', 'documentDate', 'category1'];

type BatchItemStatus = 'queued' | 'processing' | 'done' | 'error';

type BatchItem = {
  id: string;
  file?: File;
  sourceUrl?: string;
  name: string;
  status: BatchItemStatus;
  error?: string;
};

type ProcessFileResult =
  | { ok: true; id: string }
  | { ok: false; error: string; code?: string; existingId?: number | string };

type ProcessUploadResponse = Awaited<ReturnType<typeof api.upload.process>>;

const PROCESS_FAILED_HE = 'עיבוד החשבונית נכשל. נסו שוב או מלאו ידנית.';

function failedProcessResult(processResponse: ProcessUploadResponse): ProcessFileResult {
  if (processResponse.success) {
    return { ok: false, error: PROCESS_FAILED_HE };
  }
  return {
    ok: false,
    error: processResponse.error || PROCESS_FAILED_HE,
    code: processResponse.code,
    existingId: processResponse.existingId,
  };
}

function uploadErrorMessage(error: unknown): string {
  if (error instanceof Error && /failed to fetch/i.test(error.message)) {
    return 'ההעלאה נכשלה בחיבור לשרת. רעננו את הדף ונסו שוב.';
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'העלאת הקובץ נכשלה.';
}

const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { showWarning, showError, showConfirmation, setLoading } = useToast();
  const { state } = useApp();
  const { flatCategories, auth, categoryTree } = state;

  const [uploadMode, setUploadMode] = useState<'upload' | 'manual'>('upload');
  const [availableCategories2, setAvailableCategories2] = useState<FlatCategory[]>([]);
  const [availableCategories3, setAvailableCategories3] = useState<FlatCategory[]>([]);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  const [batchMode, setBatchMode] = useState<'many' | 'one'>('many');
  const [batchItems, setBatchItems] = useState<BatchItem[]>([]);
  const [imageDropActive, setImageDropActive] = useState(false);
  const [uploadPreview, setUploadPreview] = useState<PreparedInvoice | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const processingUploadRef = useRef(false);
  const batchModeRef = useRef(batchMode);
  const pendingBatchRef = useRef<BatchItem[]>([]);
  const drainingBatchRef = useRef(false);
  const [recurrencePopupPosition, setRecurrencePopupPosition] = useState({ x: 0, y: 0 });
  const [isDraggingRecurrencePopup, setIsDraggingRecurrencePopup] = useState(false);
  const [recurrencePopupDragStart, setRecurrencePopupDragStart] = useState({ x: 0, y: 0 });
  const [showRecurrencePopup, setShowRecurrencePopup] = useState(false);
  const [recurrenceDraft, setRecurrenceDraft] = useState<RecurrenceConfig>(() => defaultRecurrenceConfig());
  const recurrenceCommittedRef = useRef(false);
  const createdExpenseIdRef = useRef<string | null>(null);
  const [expense, setExpense] = useState<Expense>(() => ({
    id: '',
    serialNumber: '',
    submittedBy: auth.user?.name || auth.user?.email || '',
    vendorName: '',
    documentNumber: '',
    amount: 0,
    creationDate: '',
    documentDate: '',
    notes: '',
    category1: '',
    category2: '',
    category3: '',
    projectTag: '',
    invoiceNumber: '',
    recurrenceEnabled: false,
    recurrenceFrequency: 'monthly',
    recurrenceCount: 1,
    isCredit: false,
  }));

  const isUploadMode = uploadMode === 'upload';
  const isManyFiles = batchMode === 'many';
  const isFormLocked = isUploadMode;
  batchModeRef.current = batchMode;
  const batchBusy = batchItems.some((item) => item.status === 'queued' || item.status === 'processing');

  const isFieldRequired = (field: keyof Expense) => requiredFields.includes(field);

  const isFieldValid = (field: keyof Expense) => {
    const value = expense[field];
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    if (typeof value === 'number') {
      return value > 0;
    }
    return true;
  };

  const handleInputChange = (field: keyof Expense, value: string | number) => {
    setExpense((prev) => {
      if (field === 'amount') {
        const net = typeof value === 'number' ? value : Number(value);
        if (!Number.isFinite(net) || net <= 0) {
          return { ...prev, amount: 0, vatAmount: 0, totalAmount: undefined };
        }
        const next = prev.noVat ? amountsForNoVat(net, 0, net) : amountsFromNet(net);
        return {
          ...prev,
          amount: next.amount,
          vatAmount: next.vatAmount,
          totalAmount: next.totalAmount,
        };
      }

      if (field === 'vatAmount') {
        if (prev.noVat) return prev;
        const vat = typeof value === 'number' ? value : Number(value);
        if (!Number.isFinite(vat) || vat <= 0) {
          return { ...prev, amount: 0, vatAmount: 0, totalAmount: undefined };
        }
        const next = amountsFromVat(vat);
        return {
          ...prev,
          amount: next.amount,
          vatAmount: next.vatAmount,
          totalAmount: next.totalAmount,
        };
      }

      if (field === 'totalAmount') {
        const total = typeof value === 'number' ? value : Number(value);
        if (!Number.isFinite(total) || total <= 0) {
          return { ...prev, amount: 0, vatAmount: 0, totalAmount: undefined };
        }
        const next = prev.noVat ? amountsForNoVat(0, 0, total) : amountsFromTotal(total);
        return {
          ...prev,
          amount: next.amount,
          vatAmount: next.vatAmount,
          totalAmount: next.totalAmount,
        };
      }

      if (field === 'category1') {
        return withCategoryAmountSync(
          prev,
          { category1: String(value), category2: '', category3: '' },
          categoryTree
        );
      }

      if (field === 'category2') {
        return withCategoryAmountSync(
          prev,
          { category1: prev.category1, category2: String(value), category3: '' },
          categoryTree
        );
      }

      if (field === 'category3') {
        return withCategoryAmountSync(
          prev,
          { category1: prev.category1, category2: prev.category2, category3: String(value) },
          categoryTree
        );
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  };

  useEffect(() => {
    if (flatCategories && expense.category1) {
      const category1 = findCategoryByName(flatCategories.categories1, expense.category1);
      if (category1) {
        setAvailableCategories2(getSubcategories(flatCategories.allCategories, category1.id, 2));
      } else {
        setAvailableCategories2([]);
      }
    } else {
      setAvailableCategories2([]);
    }
  }, [expense.category1, flatCategories]);

  useEffect(() => {
    if (flatCategories && expense.category1 && expense.category2) {
      const category2 = findCategoryByName(availableCategories2, expense.category2);
      if (category2) {
        setAvailableCategories3(getSubcategories(flatCategories.allCategories, category2.id, 3));
      } else {
        setAvailableCategories3([]);
      }
    } else {
      setAvailableCategories3([]);
    }
  }, [expense.category1, expense.category2, availableCategories2, flatCategories]);

  useEffect(() => {
    const blockPage = isSaving || (isProcessingUpload && !isManyFiles);
    setLoading(blockPage, 'uploadPage');
    return () => setLoading(false, 'uploadPage');
  }, [isSaving, isProcessingUpload, isManyFiles, setLoading]);

  useEffect(() => {
    return () => {
      if (uploadPreview?.previewUrl) {
        URL.revokeObjectURL(uploadPreview.previewUrl);
      }
    };
  }, [uploadPreview]);

  const handleBack = () => {
    navigate('/');
  };

  const clearUploadPreview = () => {
    setUploadPreview((prev) => {
      if (prev?.previewUrl) {
        URL.revokeObjectURL(prev.previewUrl);
      }
      return null;
    });
  };

  const switchToManualMode = () => {
    setUploadMode('manual');
    setValidationAttempted(false);
    clearUploadPreview();
    setExpense((prev) => ({
      ...prev,
      category1: '',
      category2: '',
      category3: '',
    }));
  };

  const patchBatchItem = (id: string, patch: Partial<BatchItem>) => {
    setBatchItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const processFileToReceipt = async (file: File): Promise<ProcessFileResult> => {
    const prepared = await prepareInvoiceFile(file);
    try {
      const processResponse = await api.upload.process(file.name, prepared.blob);
      if (processResponse.success) {
        if (processResponse.data?.id) {
          return { ok: true, id: String(processResponse.data.id) };
        }
        return { ok: false, error: PROCESS_FAILED_HE };
      }
      return failedProcessResult(processResponse);
    } finally {
      if (prepared.previewUrl) {
        URL.revokeObjectURL(prepared.previewUrl);
      }
    }
  };

  const processUrlToReceipt = async (sourceUrl: string): Promise<ProcessFileResult> => {
    const processResponse = await api.upload.processUrl(sourceUrl);
    if (processResponse.success) {
      if (processResponse.data?.id) {
        return { ok: true, id: String(processResponse.data.id) };
      }
      return { ok: false, error: PROCESS_FAILED_HE };
    }
    return failedProcessResult(processResponse);
  };

  const processBatchItem = async (item: BatchItem): Promise<ProcessFileResult> => {
    if (item.sourceUrl) {
      return processUrlToReceipt(item.sourceUrl);
    }
    if (item.file) {
      return processFileToReceipt(item.file);
    }
    return { ok: false, error: 'לא נמצא קובץ או קישור.' };
  };

  const drainBatchQueue = async () => {
    if (drainingBatchRef.current) return;
    drainingBatchRef.current = true;
    setIsProcessingUpload(true);
    try {
      while (pendingBatchRef.current.length > 0) {
        const item = pendingBatchRef.current.shift();
        if (!item) break;
        patchBatchItem(item.id, { status: 'processing', error: undefined });
        try {
          const result = await processBatchItem(item);
          if (result.ok) {
            sessionStorage.setItem('expenses-reload-after-edit', '1');
            patchBatchItem(item.id, { status: 'done' });
          } else {
            patchBatchItem(item.id, { status: 'error', error: result.error });
          }
        } catch (error) {
          patchBatchItem(item.id, { status: 'error', error: uploadErrorMessage(error) });
        }
      }
    } finally {
      drainingBatchRef.current = false;
      setIsProcessingUpload(false);
      if (pendingBatchRef.current.length > 0) {
        void drainBatchQueue();
      }
    }
  };

  const enqueueFiles = (files: File[]) => {
    const picked = files.filter(Boolean);
    if (picked.length === 0) return;
    if (batchModeRef.current === 'one') {
      void handleOneByOneFile(picked[0]);
      return;
    }
    const items: BatchItem[] = picked.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      name: file.name,
      status: 'queued',
    }));
    pendingBatchRef.current.push(...items);
    setBatchItems((prev) => [...prev, ...items]);
    void drainBatchQueue();
  };

  const enqueueUrls = (urls: string[]) => {
    const picked = urls.map((url) => url.trim()).filter(Boolean);
    if (picked.length === 0) return;
    if (batchModeRef.current === 'one') {
      void handleOneByOneUrl(picked[0]);
      return;
    }
    const items: BatchItem[] = picked.map((sourceUrl) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      sourceUrl,
      name: sourceUrl,
      status: 'queued',
    }));
    pendingBatchRef.current.push(...items);
    setBatchItems((prev) => [...prev, ...items]);
    void drainBatchQueue();
  };

  const handleOneByOneUrl = async (sourceUrl: string) => {
    if (processingUploadRef.current) return;
    processingUploadRef.current = true;
    setIsProcessingUpload(true);
    try {
      const processResponse = await api.upload.processUrl(sourceUrl);
      if (processResponse.success) {
        if (processResponse.data?.id) {
          sessionStorage.setItem('expenses-reload-after-edit', '1');
          navigate(`/editor?id=${processResponse.data.id}`);
          return;
        }
        showWarning(PROCESS_FAILED_HE);
        return;
      }
      if (processResponse.code === 'duplicate_receipt' || isDuplicateReceiptError(processResponse.error)) {
        const existingId = processResponse.existingId;
        if (existingId) {
          const confirmed = await showConfirmation({
            title: 'חשבונית קיימת',
            message: processResponse.error || DUPLICATE_RECEIPT_TOAST,
            confirmText: 'פתח את ההוצאה הקיימת',
            cancelText: 'סגור',
            type: 'info',
          });
          if (confirmed === 'confirm') {
            sessionStorage.setItem('expenses-reload-after-edit', '1');
            navigate(`/editor?id=${existingId}`);
          }
          return;
        }
        showWarning(processResponse.error || DUPLICATE_RECEIPT_TOAST);
        return;
      }
      showWarning(processResponse.error || PROCESS_FAILED_HE);
    } catch (error) {
      showWarning(uploadErrorMessage(error));
    } finally {
      setIsProcessingUpload(false);
      processingUploadRef.current = false;
    }
  };

  const takeClipboardIntake = (clipboard: DataTransfer | null) => {
    const images = clipboardImageFiles(clipboard);
    if (images.length > 0) {
      enqueueFiles(images);
      return true;
    }
    const urls = extractHttpUrls(clipboard?.getData('text/plain') || clipboard?.getData('text/uri-list') || '');
    if (urls.length > 0) {
      enqueueUrls(urls);
      return true;
    }
    return false;
  };

  const handleOneByOneFile = async (file: File) => {
    if (processingUploadRef.current) return;
    processingUploadRef.current = true;
    setIsProcessingUpload(true);
    let prepared: PreparedInvoice | null = null;
    try {
      prepared = await prepareInvoiceFile(file);
      setUploadPreview((prev) => {
        if (prev?.previewUrl) {
          URL.revokeObjectURL(prev.previewUrl);
        }
        return prepared;
      });
      const processResponse = await api.upload.process(file.name, prepared.blob);
      if (processResponse.success) {
        if (processResponse.data?.id) {
          sessionStorage.setItem('expenses-reload-after-edit', '1');
          navigate(`/editor?id=${processResponse.data.id}`);
          return;
        }
        showWarning(PROCESS_FAILED_HE);
        return;
      }
      if (processResponse.code === 'duplicate_receipt' || isDuplicateReceiptError(processResponse.error)) {
        const existingId = processResponse.existingId;
        if (existingId) {
          const confirmed = await showConfirmation({
            title: 'חשבונית קיימת',
            message: processResponse.error || DUPLICATE_RECEIPT_TOAST,
            confirmText: 'פתח את ההוצאה הקיימת',
            cancelText: 'סגור',
            type: 'info',
          });
          if (confirmed === 'confirm') {
            sessionStorage.setItem('expenses-reload-after-edit', '1');
            navigate(`/editor?id=${existingId}`);
          }
          return;
        }
        showWarning(processResponse.error || DUPLICATE_RECEIPT_TOAST);
        return;
      }
      showWarning(processResponse.error || PROCESS_FAILED_HE);
    } catch (error) {
      showWarning(uploadErrorMessage(error));
    } finally {
      setIsProcessingUpload(false);
      processingUploadRef.current = false;
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setImageDropActive(true);
    } else if (e.type === 'dragleave') {
      setImageDropActive(false);
    }
  };

  const handleImageDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setImageDropActive(false);
    const uriList = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    const split = await splitDroppedInvoiceFiles(droppedFiles);
    if (split.files.length > 0) {
      enqueueFiles(split.files);
    }
    const urls = Array.from(new Set([...split.urls, ...extractHttpUrls(uriList)]));
    if (urls.length > 0) {
      enqueueUrls(urls);
    }
  };

  const handleUploadPaste = (e: React.ClipboardEvent) => {
    if (takeClipboardIntake(e.clipboardData)) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (!isUploadMode) return undefined;
    const onPaste = (event: Event) => {
      const clipboardEvent = event as ClipboardEvent;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if (takeClipboardIntake(clipboardEvent.clipboardData)) {
        event.preventDefault();
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [isUploadMode]);

  const switchBatchMode = (next: 'many' | 'one') => {
    if (batchBusy || (next === 'one' && processingUploadRef.current)) return;
    setBatchMode(next);
    if (next === 'one') {
      pendingBatchRef.current = [];
    }
  };

  const handleSave = async (action: 'stay' | 'home') => {
    if (isFormLocked || isSaving) return;

    if (createdExpenseIdRef.current) {
      if (action === 'home') {
        navigate('/');
      }
      return;
    }

    const criticalMissing = getCriticalMissingFields(expense);
    if (criticalMissing.length > 0) {
      setValidationAttempted(true);
      const labels = criticalMissing.map((field) => field.label).join(', ');
      showWarning(`אנא מלאו את השדות החסרים: ${labels}`);
      return;
    }

    if (expense.recurrenceEnabled && expense.recurrenceConfig) {
      const recurrenceGap = recurrenceCompletionMessage(expense.recurrenceConfig);
      if (recurrenceGap) {
        showWarning(recurrenceGap);
        openRecurrencePopup();
        return;
      }
    }

    setIsSaving(true);
    try {
      const response = await api.expenses.create(expense);
      if (response.success) {
        const createdId = (response.data as { id?: string } | undefined)?.id;
        createdExpenseIdRef.current = createdId ? String(createdId) : 'created';
        if (action === 'home') {
          navigate('/');
        }
        return;
      }
      if (isDuplicateReceiptError(response.error)) {
        showWarning(DUPLICATE_RECEIPT_TOAST);
        return;
      }
      const missingNotice = noticeForMissingData(response.error);
      if (missingNotice) {
        showWarning(missingNotice);
        return;
      }
      showError(`שגיאה ביצירת ההוצאה: ${response.error}`, {
        action: 'Create expense',
        error: response.error,
      });
    } catch (error) {
      showError('שגיאה ביצירת ההוצאה', {
        action: 'Create expense',
        error,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const openRecurrencePopup = () => {
    const nextConfig = expense.recurrenceConfig || defaultRecurrenceConfig(expense.documentDate);
    setRecurrenceDraft(nextConfig);
    setShowRecurrencePopup(true);
  };

  const confirmRecurrencePopup = async () => {
    const recurrenceGap = recurrenceCompletionMessage(recurrenceDraft);
    if (recurrenceGap) {
      showWarning(recurrenceGap);
      return;
    }
    if (exceedsTwoYearHorizon(recurrenceDraft)) {
      const confirmed = await showConfirmation({
        title: 'טווח החזרה',
        message:
          'טווח החזרה ארוך משנתיים. ייווצרו הוצאות לשנתיים בלבד. תקבלו תזכורת שלושה חודשים לפני תאריך הסיום.',
        confirmText: 'הבנתי',
        cancelText: 'ביטול',
        type: 'warning',
      });
      if (!confirmed) return;
    }
    setExpense((prev) => ({
      ...prev,
      recurrenceEnabled: true,
      recurrenceFrequency: recurrenceDraft.frequency,
      recurrenceCount: Math.max(1, recurrenceDraft.count || 1),
      recurrenceConfig: recurrenceDraft,
    }));
    recurrenceCommittedRef.current = true;
    setShowRecurrencePopup(false);
  };

  const removeRecurrence = () => {
    recurrenceCommittedRef.current = false;
    setExpense((prev) => ({
      ...prev,
      recurrenceEnabled: false,
      recurrenceConfig: undefined,
    }));
    setShowRecurrencePopup(false);
  };

  const closeRecurrencePopup = () => {
    setShowRecurrencePopup(false);
    if (!recurrenceCommittedRef.current) {
      setExpense((prev) => ({
        ...prev,
        recurrenceEnabled: false,
      }));
    }
  };

  useEffect(() => {
    if (!showRecurrencePopup) return undefined;
    const popupWidth = 640;
    const popupHeight = 520;
    setRecurrencePopupPosition({
      x: Math.max(16, (window.innerWidth - popupWidth) / 2),
      y: Math.max(16, (window.innerHeight - popupHeight) / 2),
    });
    return undefined;
  }, [showRecurrencePopup]);

  useEffect(() => {
    if (!showRecurrencePopup) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeRecurrencePopup();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showRecurrencePopup]);

  useEffect(() => {
    if (!isDraggingRecurrencePopup) return undefined;
    const onMove = (event: MouseEvent) => {
      setRecurrencePopupPosition({
        x: event.clientX - recurrencePopupDragStart.x,
        y: event.clientY - recurrencePopupDragStart.y,
      });
    };
    const onUp = () => setIsDraggingRecurrencePopup(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDraggingRecurrencePopup, recurrencePopupDragStart]);

  const handleRecurrencePopupHeaderMouseDown = (event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('button')) return;
    event.preventDefault();
    setIsDraggingRecurrencePopup(true);
    setRecurrencePopupDragStart({
      x: event.clientX - recurrencePopupPosition.x,
      y: event.clientY - recurrencePopupPosition.y,
    });
  };

  return (
    <div className={editorStyles.container}>
      <div className={styles.uploadHeader}>
        <div className={`${editorStyles.headerTitleRow} ${styles.uploadHeaderTitleRow}`}>
          <h1>העלאת חשבונית חדשה</h1>
        </div>
        <div className={styles.modeSwitchGroup} role="group" aria-label="בחירת מצב העלאה">
          <button
            type="button"
            className={`${styles.modeLabelButton} ${uploadMode === 'manual' ? styles.modeLabelButtonActive : ''}`.trim()}
            onClick={switchToManualMode}
          >
            ידני
          </button>
          <button
            type="button"
            className={`${styles.modeSwitch} ${isUploadMode ? styles.modeSwitchUpload : ''}`.trim()}
            onClick={() => {
              if (isUploadMode) {
                switchToManualMode();
              } else {
                setUploadMode('upload');
                setValidationAttempted(false);
              }
            }}
            role="switch"
            aria-checked={isUploadMode}
            aria-label={isUploadMode ? 'מצב העלאת חשבונית' : 'מצב ידני'}
          >
            <span className={styles.modeSwitchThumb} />
          </button>
          <button
            type="button"
            className={`${styles.modeLabelButton} ${uploadMode === 'upload' ? styles.modeLabelButtonActive : ''}`.trim()}
            onClick={() => setUploadMode('upload')}
          >
            העלאה
          </button>
        </div>
        <div className={editorStyles.headerActions}>
          <button className={editorStyles.backButton} onClick={handleBack}>
            ← חזור לרשימה
          </button>
        </div>
      </div>

      <div className={editorStyles.content}>
        <div className={`${editorStyles.formSection} ${isFormLocked ? styles.formSectionLocked : ''}`.trim()}>
          <div
            className={`${styles.formFieldsWrapper} ${isFormLocked ? styles.formFieldsLocked : ''}`.trim()}
            aria-disabled={isFormLocked}
          >
            <div className={editorStyles.editLayout}>
              <div className={editorStyles.editTopGrid}>
                <div className={editorStyles.editColumn}>
                  <div className={editorStyles.formFieldInline}>
                    <label>
                      {FIELD_LABELS.vendorName}
                      {isFieldRequired('vendorName') && <span className={editorStyles.required}>*</span>}
                    </label>
                    <div className={editorStyles.fieldControl}>
                      <VendorCombobox
                        value={expense.vendorName || ''}
                        onChange={(vendor) => handleInputChange('vendorName', vendor)}
                        disabled={isFormLocked}
                      />
                    </div>
                  </div>

                  <div className={editorStyles.formFieldInline}>
                    <label>{FIELD_LABELS.projectTag}</label>
                    <div className={editorStyles.fieldControl}>
                      <ProjectTagCombobox
                        value={expense.projectTag || ''}
                        onChange={(projectTag) => handleInputChange('projectTag', projectTag)}
                        disabled={isFormLocked}
                      />
                    </div>
                  </div>

                  <div className={`${editorStyles.formFieldInline} ${editorStyles.recurrenceFieldRow} ${styles.recurrenceInlineRow}`}>
                    <label className={styles.recurrenceCheckboxOnly}>
                      <input
                        type="checkbox"
                        checked={Boolean(expense.recurrenceEnabled) || showRecurrencePopup}
                        onChange={() => {
                          if (isFormLocked) return;
                          openRecurrencePopup();
                        }}
                        disabled={isFormLocked}
                        aria-label="הוצאה חוזרת"
                      />
                      <span className={styles.recurrenceText}>הוצאה חוזרת</span>
                    </label>
                  </div>

                  <div className={`${editorStyles.formFieldInline} ${editorStyles.recurrenceFieldRow} ${styles.recurrenceInlineRow}`}>
                    <label className={styles.recurrenceCheckboxOnly}>
                      <input
                        type="checkbox"
                        checked={Boolean(expense.isCredit)}
                        onChange={(event) => {
                          if (isFormLocked) return;
                          setExpense((prev) => ({ ...prev, isCredit: event.target.checked }));
                        }}
                        disabled={isFormLocked}
                        aria-label={FIELD_LABELS.credit}
                      />
                      <span className={`${styles.recurrenceText} ${expense.isCredit ? styles.creditCheckboxText : ''}`.trim()}>
                        {FIELD_LABELS.credit}
                      </span>
                    </label>
                  </div>
                </div>

                <div className={`${editorStyles.editColumn} ${editorStyles.editColumnAmounts}`}>
                  <div className={`${editorStyles.formFieldInline} ${!expense.amount ? editorStyles.fieldMissing : ''}`}>
                    <label>
                      {FIELD_LABELS.amountShort}
                      {isFieldRequired('amount') && <span className={editorStyles.required}>*</span>}
                    </label>
                    <div className={editorStyles.fieldControl}>
                      <MoneyInput
                        value={expense.amount}
                        onChange={(next) => handleInputChange('amount', next)}
                        emptyIfZero
                        className={!isFieldValid('amount') ? editorStyles.invalidField : ''}
                      />
                    </div>
                  </div>

                  <div className={editorStyles.formFieldInline}>
                    <label>{FIELD_LABELS.vatAmountShort}</label>
                    <div className={editorStyles.fieldControl}>
                      {expense.noVat ? (
                        <input
                          type="text"
                          readOnly
                          value={NO_VAT_DISPLAY}
                          dir="ltr"
                          className={`${editorStyles.readOnlyField} ${editorStyles.ltrField}`}
                          title="ללא מע״מ"
                        />
                      ) : (
                        <MoneyInput
                          value={expense.vatAmount || 0}
                          onChange={(next) => handleInputChange('vatAmount', next)}
                          emptyIfZero
                        />
                      )}
                    </div>
                  </div>

                  <div className={editorStyles.formFieldInline}>
                    <label>{FIELD_LABELS.totalAmountShort}</label>
                    <div className={editorStyles.fieldControl}>
                      <MoneyInput
                        value={Number(expense.totalAmount) || 0}
                        onChange={(next) => handleInputChange('totalAmount', next)}
                        emptyIfZero
                        title="סכום כולל (כולל מע״מ)"
                      />
                    </div>
                  </div>
                </div>

                <div className={`${editorStyles.editColumn} ${editorStyles.editColumnDates}`}>
                  <div className={editorStyles.formFieldInline}>
                    <label>{FIELD_LABELS.creationTimestamp}</label>
                    <div className={editorStyles.fieldControl}>
                      <input
                        type="text"
                        value={expense.creationTimestamp ? formatMetricaDate(expense.creationTimestamp) : ''}
                        readOnly
                        className={`${editorStyles.readOnlyField} ${editorStyles.ltrField} ${editorStyles.uploadDateField}`}
                        aria-label={FIELD_LABELS.creationTimestamp}
                      />
                    </div>
                  </div>

                  <div className={`${editorStyles.formFieldInline} ${!expense.documentDate ? editorStyles.fieldMissing : ''}`}>
                    <label>
                      {FIELD_LABELS.documentDate}
                      {isFieldRequired('documentDate') && <span className={editorStyles.required}>*</span>}
                    </label>
                    <div className={editorStyles.fieldControl}>
                      <MetricaDateInput
                        value={expense.documentDate || ''}
                        onChange={(isoDate) => handleInputChange('documentDate', isoDate)}
                        ariaLabel={FIELD_LABELS.documentDate}
                        invalid={!isFieldValid('documentDate')}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className={editorStyles.editCategoriesRow}>
                <div className={`${editorStyles.formFieldInline} ${!expense.category1 ? editorStyles.fieldMissing : ''}`}>
                  <label>
                    {FIELD_LABELS.category1Short}
                    {isFieldRequired('category1') && <span className={editorStyles.required}>*</span>}
                  </label>
                  <div className={editorStyles.fieldControl}>
                    <SearchableSelect
                      value={expense.category1}
                      onChange={(next) => {
                        setValidationAttempted(false);
                        handleInputChange('category1', next);
                      }}
                      options={optionsFromNames(flatCategories?.categories1 || [])}
                      ariaLabel={FIELD_LABELS.category1Short}
                      disabled={isFormLocked}
                      invalid={validationAttempted && !isFieldValid('category1')}
                      placeholder="בחר קטגוריה"
                    />
                  </div>
                </div>
                <div
                  className={`${editorStyles.formFieldInline} ${
                    expense.category1 && !expense.category2?.trim() ? styles.fieldSoftMissing : ''
                  }`.trim()}
                >
                  <label>{FIELD_LABELS.category2Short}</label>
                  <div className={editorStyles.fieldControl}>
                    <SearchableSelect
                      value={expense.category2}
                      onChange={(next) => handleInputChange('category2', next)}
                      options={optionsFromNames(availableCategories2)}
                      ariaLabel={FIELD_LABELS.category2Short}
                      disabled={isFormLocked || !expense.category1}
                      emptyOption={{ value: '', label: 'n/a' }}
                    />
                  </div>
                </div>
                <div className={editorStyles.formFieldInline}>
                  <label>{FIELD_LABELS.category3Short}</label>
                  <div className={editorStyles.fieldControl}>
                    <SearchableSelect
                      value={expense.category3}
                      onChange={(next) => handleInputChange('category3', next)}
                      options={optionsFromNames(availableCategories3)}
                      ariaLabel={FIELD_LABELS.category3Short}
                      disabled={isFormLocked || !expense.category2}
                      emptyOption={{ value: '', label: 'n/a' }}
                    />
                  </div>
                </div>
              </div>

              <div className={editorStyles.editNotesRow}>
                <label className={editorStyles.notesLabel}>{FIELD_LABELS.notes}</label>
                <div className={editorStyles.notesField}>
                  <textarea
                    dir="auto"
                    value={expense.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    rows={5}
                  />
                </div>

                <div className={editorStyles.notesSideFields}>
                  <div className={`${editorStyles.formFieldInline} ${!expense.documentNumber ? editorStyles.fieldMissing : ''}`}>
                    <label>{FIELD_LABELS.invoiceNumber}</label>
                    <div className={editorStyles.fieldControl}>
                      <input
                        type="text"
                        value={expense.documentNumber || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setExpense((prev) => ({
                            ...prev,
                            documentNumber: value,
                            invoiceNumber: value,
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className={editorStyles.formFieldInline}>
                    <label>{FIELD_LABELS.submittedBy}</label>
                    <div className={editorStyles.fieldControl}>
                      <input
                        type="text"
                        value={expense.submittedBy || ''}
                        readOnly
                        className={editorStyles.readOnlyField}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

          {!isUploadMode && (
          <div className={editorStyles.actionButtons}>
            <button
              type="button"
              className={editorStyles.saveButton}
              disabled={isFormLocked || isSaving}
              onClick={() => handleSave('stay')}
            >
              שמור
            </button>
          </div>
          )}
          </div>
        </div>

        <div className={editorStyles.imageSection}>
          <div className={editorStyles.imageSectionBody}>
            {isUploadMode ? (
              <div className={styles.uploadBox}>
                <div
                  className={`${editorStyles.imageUpload} ${styles.uploadDrop} ${imageDropActive ? editorStyles.imageUploadDropActive : ''}`.trim()}
                  onDragEnter={handleImageDrag}
                  onDragLeave={handleImageDrag}
                  onDragOver={handleImageDrag}
                  onDrop={handleImageDrop}
                  onPaste={handleUploadPaste}
                >
                  <div
                    className={styles.batchSwitchGroup}
                    role="group"
                    aria-label="מצב העלאת קבצים"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <button
                      type="button"
                      className={`${styles.modeLabelButton} ${!isManyFiles ? styles.modeLabelButtonActive : ''}`.trim()}
                      onClick={() => switchBatchMode('one')}
                      disabled={batchBusy}
                    >
                      אחד אחד
                    </button>
                    <button
                      type="button"
                      className={`${styles.modeSwitch} ${isManyFiles ? styles.modeSwitchUpload : ''}`.trim()}
                      onClick={() => switchBatchMode(isManyFiles ? 'one' : 'many')}
                      disabled={batchBusy}
                      role="switch"
                      aria-checked={isManyFiles}
                      aria-label={isManyFiles ? 'מצב קבצים רבים' : 'מצב אחד אחד'}
                    >
                      <span className={styles.modeSwitchThumb} />
                    </button>
                    <button
                      type="button"
                      className={`${styles.modeLabelButton} ${isManyFiles ? styles.modeLabelButtonActive : ''}`.trim()}
                      onClick={() => switchBatchMode('many')}
                      disabled={batchBusy}
                    >
                      קבצים רבים
                    </button>
                  </div>
                  {!isManyFiles && uploadPreview ? (
                    <div className={editorStyles.imagePreview}>
                      <img
                        src={uploadPreview.previewUrl}
                        alt="Invoice preview"
                        className={editorStyles.clickableImage}
                      />
                      {uploadPreview.pdfFirstPageOnly && (
                        <p className={editorStyles.previewOnlyNotice}>רק העמוד הראשון של ה-PDF יעובד.</p>
                      )}
                      {!isProcessingUpload && (
                        <button
                          type="button"
                          className={editorStyles.uploadButton}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          החלף קובץ
                        </button>
                      )}
                    </div>
                  ) : (
                    <label htmlFor="computerInvoiceUpload" className={`${editorStyles.uploadPlaceholder} ${styles.uploadDropLabel}`}>
                      <div className={editorStyles.uploadIcon}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="currentColor"/>
                        </svg>
                      </div>
                      <p className={editorStyles.emptyImageMessage}>
                        {isManyFiles ? 'לחצו, גררו או הדביקו חשבוניות' : 'לחצו, גררו או הדביקו חשבונית'}
                      </p>
                      <p className={styles.uploadHint}>
                        {isManyFiles
                          ? 'קובץ, צילום מסך או קישור. JPEG, PNG, WebP או PDF.'
                          : 'קובץ, צילום מסך או קישור. JPEG, PNG, WebP או PDF. ב-PDF יעובד העמוד הראשון בלבד.'}
                      </p>
                    </label>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf,.jpg,.jpeg,.png,.webp,.pdf"
                    multiple={isManyFiles}
                    onChange={(event) => {
                      enqueueFiles(Array.from(event.target.files || []));
                      event.target.value = '';
                    }}
                    className={editorStyles.fileInput}
                    id="computerInvoiceUpload"
                    disabled={!isManyFiles && isProcessingUpload}
                  />
                </div>
                {isManyFiles && batchItems.length > 0 && (
                  <ul className={styles.uploadedFilesList}>
                    {batchItems.map((item) => (
                      <li
                        key={item.id}
                        className={`${styles.uploadedFileItem} ${
                          item.status === 'error' ? styles.uploadedFileItemError : ''
                        }`.trim()}
                      >
                        <span className={styles.uploadedFileName}>{item.name}</span>
                        <span className={styles.uploadedFileStatus}>
                          {item.status === 'queued' && 'ממתין'}
                          {item.status === 'processing' && 'מעבד'}
                          {item.status === 'done' && 'נשמר'}
                          {item.status === 'error' && (item.error || 'שגיאה')}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className={styles.manualImagePlaceholder} role="status">
                <p>במצב ידני אין צורך בקובץ. החליפו ל״העלאה״ כדי לצרף חשבונית מהמחשב.</p>
              </div>
            )}
          </div>
          <div className={editorStyles.imageSectionActions}>
            {isUploadMode && isManyFiles ? (
              <button
                type="button"
                className={editorStyles.saveButton}
                onClick={() => {
                  sessionStorage.setItem('expenses-reload-after-edit', '1');
                  navigate('/');
                }}
              >
                חזור לרשימה
              </button>
            ) : !isUploadMode ? (
              <button
                type="button"
                className={editorStyles.saveButton}
                disabled={isFormLocked || isSaving}
                onClick={() => handleSave('home')}
              >
                שמור וחזור לראשי
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {showRecurrencePopup && (
        <RecurrencePopup
          config={recurrenceDraft}
          onChange={setRecurrenceDraft}
          onConfirm={confirmRecurrencePopup}
          onCancel={closeRecurrencePopup}
          onRemove={removeRecurrence}
          position={recurrencePopupPosition}
          onHeaderMouseDown={handleRecurrencePopupHeaderMouseDown}
        />
      )}
    </div>
  );
};

export default UploadPage;
